import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, type DropdownMenuItem } from '@/components/ui';
import type { DragSource, IllegalDropInfo } from './types';

export interface HierarchyRowDragHandlers {
    onDragStart: (source: DragSource) => void;
    onDragOver: (rowId: string, rowKind: 'org' | 'level', rect: DOMRect, clientY: number) => void;
    onDrop: () => void;
    onDragEnd: () => void;
    onKeyDown: (e: React.KeyboardEvent, source: DragSource) => void;
}

/** Identity of the row — never changes while the row is mounted. */
export interface HierarchyRowIdentity {
    id: string;
    kind: 'org' | 'level';
    depth: number;
    label: string;
    code?: string;
}

/** Everything that renders to the right of the name, plus row-level styling. */
export interface HierarchyRowState {
    countLabel?: string;
    /** Pill badge before the count — "ngưng", "đang chuyển" in frame 28. */
    badge?: string;
    /**
     * EAA-1838: the đơn vị's type name ("Sở", "Trường"). Replaced `tierLabel`.
     * Absent only while the vocabulary is still loading — every đơn vị has a type,
     * unlike the optional tier this replaced, so an empty chip slot is a transient
     * state rather than the normal one it used to be.
     */
    unitTypeLabel?: string;
    /** True when that type has been hidden (`is_active: false`) — greys the chip. */
    unitTypeHidden?: boolean;
    statusDot?: 'on' | 'off' | 'warn';
    muted?: boolean;
    /** Dimmed ancestor context in frame 33's search-result tree. */
    dimmed?: boolean;
    /** This row is the drag source, staying in place at 50% opacity (frame 28). */
    moving?: boolean;
    selected?: boolean;
    /** Being hovered as a re-parent (drop-onto) target. */
    ontoActive?: boolean;
    illegal?: IllegalDropInfo | null;
}

/**
 * EAA-1818: lazy-expand controls. `expandable` is true whenever children MIGHT
 * exist — for a lazily loaded tree that is "not known to be a leaf", so the
 * twisty renders before the first fetch (see `types.ts`, `childrenLoaded`).
 */
export interface HierarchyRowExpand {
    expandable: boolean;
    expanded: boolean;
    loading: boolean;
    onToggle: () => void;
}

/** Inline rename at the row — frame 30's "Đổi tên ngay tại dòng". */
export interface HierarchyRowRename {
    active: boolean;
    draft: string;
    error: string | null;
    pending: boolean;
    onDraftChange: (value: string) => void;
    onCommit: () => void;
    onCancel: () => void;
}

export interface HierarchyRowProps {
    row: HierarchyRowIdentity;
    state?: HierarchyRowState;
    expand?: HierarchyRowExpand;
    rename?: HierarchyRowRename;
    actions?: DropdownMenuItem[];
    draggable?: boolean;
    onSelect?: () => void;
    /** F2 on a focused row starts an inline rename (frame 30). */
    onRenameShortcut?: () => void;
    /** ⌘M / Ctrl+M on a focused row opens "Chuyển đến…" (frame 30). */
    onMoveShortcut?: () => void;
    dragHandlers: HierarchyRowDragHandlers;
}

/** 20px indent guide per depth level — frame 28's `width:20px; border-left:1px solid`. */
function IndentGuides({ depth }: { depth: number }) {
    if (depth <= 0) return null;
    return (
        <>
            {Array.from({ length: depth }).map((_, i) => (
                <span key={i} className="hz-indent" />
            ))}
        </>
    );
}

function DragHandleIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="hz-handle"
            aria-hidden="true"
        >
            <circle cx="9" cy="6" r="1.2" />
            <circle cx="15" cy="6" r="1.2" />
            <circle cx="9" cy="12" r="1.2" />
            <circle cx="15" cy="12" r="1.2" />
            <circle cx="9" cy="18" r="1.2" />
            <circle cx="15" cy="18" r="1.2" />
        </svg>
    );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
    return (
        <svg
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={expanded ? 'hz-twisty-icon hz-twisty-icon--open' : 'hz-twisty-icon'}
        >
            <path d="m9 6 6 6-6 6" />
        </svg>
    );
}

function initialsOf(label: string): string {
    return (
        label
            .split(/[\s–-]+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase())
            .join('') || '·'
    );
}

/**
 * EAA-1818: deliberately NOT wrapped in `React.memo`, and that is a considered
 * choice rather than an omission.
 *
 * `React.memo` only pays when every prop is referentially stable. Almost all of
 * this row's props are per-row DERIVED state — is it selected, is it the drag
 * source, is it the illegal drop target, is it mid-rename — so the caller would
 * have to either pass ~24 primitives (well past this repo's 6–8 prop ceiling,
 * and unreadable) or rebuild the grouped objects each render, in which case the
 * comparison always fails and memo is pure overhead. The alternative that WOULD
 * pay — a per-node memoized wrapper owning its own slice of vm state — needs a
 * per-node subscription this hook's shape does not offer.
 *
 * What makes that acceptable here, unlike `web-content`'s ModuleTreeView: this
 * tree renders a flat map over `visibleNodes` (one lazily loaded level per
 * expansion, tens of rows, no recursion in the component graph and no
 * virtualization), and the pointer path that re-renders on every move is already
 * `requestAnimationFrame`-throttled in `useHierarchyDrag`. Revisit if the tree
 * ever loads whole subtrees eagerly.
 */
export function HierarchyRow({
    row,
    state,
    expand,
    rename,
    actions,
    draggable = true,
    onSelect,
    onRenameShortcut,
    onMoveShortcut,
    dragHandlers,
}: HierarchyRowProps) {
    const { t } = useTranslation();
    const { id, kind, depth, label, code } = row;
    const renameActive = rename?.active ?? false;
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (renameActive) inputRef.current?.select();
    }, [renameActive]);

    const dragSourceFor = useCallback((): DragSource => ({ kind, id, label }), [kind, id, label]);

    const handleDragStart = useCallback(
        (e: React.DragEvent) => {
            e.dataTransfer.effectAllowed = 'move';
            dragHandlers.onDragStart(dragSourceFor());
        },
        [dragHandlers, dragSourceFor]
    );
    const handleDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            dragHandlers.onDragOver(id, kind, e.currentTarget.getBoundingClientRect(), e.clientY);
        },
        [dragHandlers, id, kind]
    );
    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            dragHandlers.onDrop();
        },
        [dragHandlers]
    );
    const handleDragEnd = useCallback(() => dragHandlers.onDragEnd(), [dragHandlers]);

    /**
     * EAA-1818: F2 and ⌘M/Ctrl+M are handled here rather than on a document
     * listener because both act on the FOCUSED row, and the row already owns
     * focus. `useCommandPalette`'s document-level ⌘K is the right pattern for a
     * global chord with no target; a per-row chord read from the document would
     * have to re-derive which row is focused, which the DOM already knows.
     * `stopPropagation` is not used: the drag state machine's Space/Arrow keys
     * still need to reach `onKeyDown` below.
     */
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'F2' && onRenameShortcut) {
                e.preventDefault();
                onRenameShortcut();
                return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'm' && onMoveShortcut) {
                e.preventDefault();
                onMoveShortcut();
                return;
            }
            dragHandlers.onKeyDown(e, dragSourceFor());
        },
        [dragHandlers, dragSourceFor, onRenameShortcut, onMoveShortcut]
    );

    const handleRenameKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            // Enter saves, Esc abandons (frame 30). Both must stop here so the
            // tree's own Enter (= complete drop) never sees them.
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                rename?.onCommit();
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                rename?.onCancel();
                return;
            }
            e.stopPropagation();
        },
        [rename]
    );

    const handleToggle = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            expand?.onToggle();
        },
        [expand]
    );

    const rowClassName = [
        'hz-row',
        state?.muted ? 'hz-row--muted' : '',
        state?.dimmed ? 'hz-row--dimmed' : '',
        state?.moving ? 'hz-row--moving' : '',
        state?.selected ? 'hz-row--selected' : '',
        state?.ontoActive ? 'hz-row--onto' : '',
        state?.illegal ? `hz-row--illegal-${state.illegal.tone}` : '',
        renameActive ? 'hz-row--renaming' : '',
    ]
        .filter(Boolean)
        .join(' ');

    let twisty: React.ReactNode = <span className="hz-twisty-spacer" />;
    if (expand?.expandable) {
        twisty = (
            <button
                type="button"
                className="hz-twisty"
                aria-label={
                    expand.expanded
                        ? t('console.hierarchy.row.collapseAriaLabel', { label })
                        : t('console.hierarchy.row.expandAriaLabel', { label })
                }
                aria-expanded={expand.expanded}
                onClick={handleToggle}
            >
                {expand.loading ? (
                    <span className="hz-twisty-loading" />
                ) : (
                    <ChevronIcon expanded={expand.expanded} />
                )}
            </button>
        );
    }

    let body: React.ReactNode;
    if (renameActive && rename) {
        body = (
            <>
                <input
                    ref={inputRef}
                    className="hz-row-input"
                    type="text"
                    value={rename.draft}
                    aria-label={t('console.hierarchy.row.renameAriaLabel', { label })}
                    disabled={rename.pending}
                    onChange={(e) => rename.onDraftChange(e.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    autoFocus
                />
                {code && <code className="hz-code hz-code--locked">{code}</code>}
            </>
        );
    } else {
        body = (
            <>
                <span className="hz-name">{label}</span>
                {state?.unitTypeLabel && (
                    <span
                        className={
                            state.unitTypeHidden
                                ? 'hz-unit-type-tag hz-unit-type-tag--hidden'
                                : 'hz-unit-type-tag'
                        }
                        title={
                            state.unitTypeHidden
                                ? t('console.hierarchy.row.unitTypeTitleHidden', {
                                      label: state.unitTypeLabel,
                                  })
                                : t('console.hierarchy.row.unitTypeTitle', {
                                      label: state.unitTypeLabel,
                                  })
                        }
                    >
                        {state.unitTypeLabel}
                    </span>
                )}
                {code && <code className="hz-code">{code}</code>}
                {state?.moving && (
                    <span className="hz-moving-tag">{t('console.hierarchy.row.movingTag')}</span>
                )}
                {state?.badge && <span className="hz-moving-tag">{state.badge}</span>}
                {state?.illegal && (
                    <span className={`hz-illegal-text hz-illegal-text--${state.illegal.tone}`}>
                        {state.illegal.reason}
                    </span>
                )}
                {state?.countLabel !== undefined && (
                    <span className="hz-count">{state.countLabel}</span>
                )}
                {state?.statusDot && <span className={`hz-dot hz-dot--${state.statusDot}`} />}
                {actions && actions.length > 0 && (
                    <DropdownMenu
                        triggerAriaLabel={t('console.hierarchy.row.actionsAriaLabel', { label })}
                        items={actions}
                    />
                )}
                {(!actions || actions.length === 0) && <span className="hz-kebab-spacer" />}
            </>
        );
    }

    return (
        <span className="hz-row-wrap">
            <IndentGuides depth={depth} />
            <span
                className={rowClassName}
                data-row-id={id}
                data-row-kind={kind}
                draggable={draggable && !renameActive}
                tabIndex={0}
                role="treeitem"
                aria-selected={state?.selected || undefined}
                aria-expanded={expand?.expandable ? expand.expanded : undefined}
                onDragStart={draggable && !renameActive ? handleDragStart : undefined}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                onKeyDown={handleKeyDown}
                onClick={onSelect}
            >
                <DragHandleIcon />
                {twisty}
                <span className="hz-avatar" data-muted={state?.muted || undefined}>
                    {initialsOf(label)}
                </span>
                {body}
            </span>
            {renameActive && rename?.error && (
                <span className="hz-row-error" role="alert">
                    {rename.error}
                </span>
            )}
        </span>
    );
}

export default HierarchyRow;
